import React, { useState } from 'react';
import { css } from '@emotion/css';
import TemplateFormSectionModel from '../../../models/templating/api/TemplateFormSectionModel';
import { color_text_link, color_text_default, color_variants_ocean_light_1, color_text_light } from '../../../constants/colors';
import _ from 'lodash';
import { Droppable, DragDropContext, Draggable, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import { reorder } from '../../../models/extensions';
import SearchField from '../../../components/forms/SearchField';

interface TemplateSectionSelectorProps {
    sections: TemplateFormSectionModel[]
    activeSection: TemplateFormSectionModel
    isStartSelected?: boolean
    onChange: (section: TemplateFormSectionModel) => void
    onSort: (sections: TemplateFormSectionModel[]) => void
    onStartSelect: () => void
    onFieldSelect?: (fieldId: string) => void
}

const TemplateSectionSelector: React.SFC<TemplateSectionSelectorProps> = (props) => {
    const [sectionSearch, setSectionSearch] = useState('');

    const handleDragEnd = (result: DropResult, provider: ResponderProvided) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        const sections = _.sortBy(props.sections ?? [], s => s.priority)
        props.onSort(reorder(sections, result.source.index, result.destination.index));
    }

    const isFiltering = sectionSearch.trim().length > 0;
    const sortedSections = _.sortBy(props.sections ?? [], s => s.priority);

    // When filtering, include a section if its title matches OR any of its fields match.
    // For each included section, also compute which fields matched so we can show them.
    const filteredResults = isFiltering
        ? sortedSections.reduce<Array<{ section: TemplateFormSectionModel, matchingFieldIds: string[] }>>((acc, s) => {
            const query = sectionSearch.toLowerCase();
            const titleMatch = s.title?.toLowerCase().includes(query);
            const matchingFields = (s.templateFormFields ?? []).filter(f =>
                f.title?.toLowerCase().includes(query) ||
                f.associatedVariable?.toLowerCase().includes(query)
            );
            if (titleMatch || matchingFields.length > 0) {
                acc.push({ section: s, matchingFieldIds: matchingFields.filter(f => !!f.id).map(f => f.id as string) });
            }
            return acc;
        }, [])
        : [];

    return (
        <div className={containerStyle}>
            <SearchField
                placeholder="Search sections..."
                value={sectionSearch}
                onChange={e => setSectionSearch(e.target.value)}
                handleClear={() => setSectionSearch('')}
                className={searchFieldStyle}
                boxType="square"
            />
            <div className={`section-item ${props.isStartSelected ? 'active' : ''}`} onClick={props.onStartSelect}>
                <p>Overview</p>
            </div>
            {isFiltering
                ? filteredResults.map(({ section: s, matchingFieldIds }) => (
                    <div key={s.id}>
                        <div
                            className={`section-item ${props.activeSection == s ? 'active' : ''}`}
                            onClick={() => props.onChange(s)}>
                            <p>{s?.title}</p>
                        </div>
                        {matchingFieldIds.map(fieldId => {
                            const field = (s.templateFormFields ?? []).find(f => f.id === fieldId);
                            if (!field) return null;
                            return (
                                <div
                                    key={fieldId}
                                    className="field-result-item"
                                    onClick={() => props.onFieldSelect?.(fieldId)}>
                                    <p>{field.title || <em>Untitled</em>}</p>
                                </div>
                            );
                        })}
                    </div>
                ))
                : <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId={"sections"} type="TEMPLATE_SECTION">
                        {
                            (provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}>
                                    {sortedSections.map((s, i) => (
                                        <Draggable draggableId={s?.id} index={i} key={s.id}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className={`section-item ${props.activeSection == s ? 'active' : ''}`}
                                                    onClick={() => props.onChange(s)}>
                                                    <p>{s?.title}</p>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )
                        }
                    </Droppable>
                </DragDropContext>
            }
        </div>
    );
}

const searchFieldStyle = css`
    margin-bottom: 8px;
`

const containerStyle = css`

    padding: 16px;

    .section-item {
        display: flex;
        align-items: center;
        border-radius: 4px;
        height: 48px;
        padding: 0 16px;
        cursor: pointer;
        p {
            font-size: 14px;
            line-height: 20px;
            color: ${color_text_default};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        img {
            margin-right: 0;
            margin-left: auto;
            width: 24px;
            height: 24px;
        }

        .selected-indicator {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: ${color_text_link};
            margin-right: 8px;
            margin-left: auto;
        }
        
        &.active {
            background: ${color_variants_ocean_light_1};
            p {
                color: ${color_text_link}
            }
        }

        &.disabled {
            img {
                display: none;
            }
            p {
                color: ${color_text_light};
            }
        }
    }

    .field-result-item {
        display: flex;
        align-items: center;
        border-radius: 4px;
        height: 40px;
        padding: 0 16px 0 32px;
        cursor: pointer;
        p {
            font-size: 13px;
            line-height: 18px;
            color: ${color_text_light};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        &:hover {
            background: ${color_variants_ocean_light_1};
            p {
                color: ${color_text_link};
            }
        }
    }

`

export default TemplateSectionSelector;